import {AfterViewInit, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {CommonService} from '../../services/common.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatStepper} from '@angular/material';
import {MatDialogRef} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {SelectionModel} from '@angular/cdk/collections';
import {ToastMessagesService} from '../../services/toast-messages.service';

interface SalePoints {
  id: number;
  customer_name: string;
  user_id?: number;
  name?: string;
}

interface User {
  id: number;
  name: string;
  sales_group_names?: any;
  sale_points?: [];
  initialSalePoints?: [];
  numberOfSalePoints?: number;
}

interface Role {
  id: number;
  name: string;
  alternate_name: string;
  is_mobile_user: number;
  totalUsers?: User[];
  selectedUsers?: User[];
  selectedSalePoints?: any[];
  salePointsForRemoval?: any[];
  usersForRemoval?: any[];
}

@Component({
  styleUrls: ['./questionnair-assignement-wizard-dialog.component.scss'],
  templateUrl: './questionnair-assignement-wizard-dialog.component.html'
})
export class QuestionnairAssignementWizardDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('stepper', {static: false}) stepper: MatStepper;
  @ViewChild('userSort', {static: true}) userSort: MatSort;
  @ViewChild('userPaginator', {static: true}) userPaginator: MatPaginator;
  @ViewChild('spSort', {static: true}) spSort: MatSort;
  @ViewChild('spPaginator', {static: false}) spPaginator: MatPaginator;
  categoryId: number;
  details: any;
  detailsId: number;
  filter: string;
  isLoading: boolean;
  mode: number;
  nextLoading: boolean;
  roleFormGroup: FormGroup;
  roles: Role[] = [];
  salePointSelection: any;
  salePoints: SalePoints[] = [];
  salePointsDataSource: MatTableDataSource<SalePoints>;
  salePointsDisplayColumns = ['select', 'customer_name'];
  salePointsLoading: boolean;
  selectedRowIndex = -1;
  statusId: number;
  title: string;
  userDisplayColumns: any[];
  userSelection: any;
  users: User[] = [];
  usersDataSource: MatTableDataSource<User>;
  usersFormGroup: FormGroup;
  selectedUser: User;

  constructor(private commonService: CommonService,
              private formBuilder: FormBuilder,
              private matDialogRef: MatDialogRef<QuestionnairAssignementWizardDialogComponent>,
              private toastMessagesService: ToastMessagesService,
              @Inject(MAT_DIALOG_DATA) public data: any) {
    this.title = data.title;
    this.categoryId = data.categoryId;
    this.statusId = data.statusId;
    this.mode = data.mode;
    this.detailsId = data.detailsId;
    this.userSelection = new SelectionModel<User>(true, []);
    this.salePointSelection = new SelectionModel<SalePoints>(true, []);
    this.salePointsLoading = false;
    this.nextLoading = false;

    this.userDisplayColumns = data.categoryId !== 3 ? ['select', 'name', 'numberOfSalePoints'] : ['select', 'name'];
    this.roleFormGroup = this.formBuilder.group({
      roles: ['', Validators.required]
    });
    this.usersFormGroup = this.formBuilder.group({
      users: ['', Validators.required]
    });
    this.usersDataSource = new MatTableDataSource<User>(this.users);
    this.salePointsDataSource = new MatTableDataSource<SalePoints>();
    this.isLoading = false;

  }

  public async ngOnInit(): Promise<void> {
    this.isLoading = true;
    try {
      await this.fetchRoles();
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    }
  }

  ngAfterViewInit() {
    this.usersDataSource.paginator = this.commonService.setPaginatorTexts(this.userPaginator);
    if (this.categoryId !== 3) {
      this.salePointsDataSource.paginator = this.commonService.setPaginatorTexts(this.spPaginator);
    }
  }

  highlight(row) {
    this.selectedRowIndex = row.id;
  }

  async fetchRoles() {
    try {
      this.isLoading = true;
      if (this.categoryId === 3) {
        this.roles = await this.commonService.getListWithParameters(`role`, `is_mobile_user=1`) as Role[];
      } else {
        this.roles = await this.commonService.getListWithParameters(`role`, `id=4 OR id=5 OR id=6 OR id=7 OR id=8 OR id=9`) as Role[];
      }

      for (const role of this.roles) {
        role.totalUsers = [];
        role.selectedUsers = [];
        role.selectedSalePoints = [];
      }
    } catch (e) {
      throw e;
    } finally {
      this.isLoading = false;
    }
  }

  async fetchRoutingDetails(roleId: number) {
    try {
      return await this.commonService.getList(`questionnaire/routed/${this.detailsId}/${roleId}`);
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    }
  }

  async next() {
    const role = this.roleFormGroup.controls.roles.value;

    switch (this.stepper.selectedIndex) {
      case 0:
        if (role) {
          await this.initializeUserStep();
          this.stepper.next();
        } else {
          return;
        }
        break;
      case 1:
        const selectedUsers = this.categoryId !== 3 ?
          this.userSelection.selected.filter(user => user.numberOfSalePoints > 0) :
          this.userSelection.selected;
        this.userSelection = new SelectionModel<User>(true, selectedUsers);
        let users = [];
        if (this.categoryId !== 3) {
          users = this.userSelection.selected.map(user =>
            ({
              id: user.id,
              questionnaire_user_id: this.details && this.details.users.some(u => u.user_id === user.id) ?
                this.details.users.find(u => u.user_id === user.id).questionnaire_user_id : null,
              sale_points: user.sale_points.length === 0 && user.initialSalePoints.length > 0 ?
                user.initialSalePoints.map(sp => sp.id) : user.sale_points.map(sp => sp.id),
            }));
        } else {
          users = this.userSelection.selected.map(user =>
            ({
              id: user.id,
              questionnaire_user_id: this.details && this.details.users.some(u => u.user_id === user.id) ?
                this.details.users.find(u => u.user_id === user.id).questionnaire_user_id : null,
              sale_points: []
            }));
        }
        const requestObject = {
          category_id: this.categoryId,
          status_id: this.statusId,
          role_id: role.id,
          users
        };
        this.usersFormGroup.controls.users.setValue(users);
        this.stepper.next();
        await this.submitRequest(role.id, requestObject);
        break;
    }


  }

  previous() {
    this.salePointsDataSource = new MatTableDataSource<SalePoints>();
    this.stepper.previous();
  }

  async initializeUserStep() {
    try {
      // this.isLoading = true;
      this.nextLoading = true;


      this.users = await this.fetchUsers();
      for (const user of this.users) {
        user.sale_points = [];
        user.numberOfSalePoints = 0;
      }

      if (this.mode === 1) {
        await this.distributeUsersAndSalePoints();
      }
      this.usersDataSource = new MatTableDataSource<User>(this.users);
      this.usersDataSource.paginator = this.userPaginator;
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    } finally {
      this.nextLoading = false;
    }
    // this.details = await this.fetchRoutingDetails();

  }

  async fetchUsers(): Promise<User[]> {
    const role = this.roleFormGroup.controls.roles.value;
    this.filter = role.id === 4 ? 'inspector_id' : role.id === 5 ? 'regional_id' : 'user_id';
    role.totalUsers = await this.commonService.getListWithParameters(`user`, `role_id=${role.id}`);
    return await this.commonService.getListWithParameters(`user`, `role_id=${role.id}`) as User[];
  }

  async distributeUsersAndSalePoints() {

    const role = this.roleFormGroup.controls.roles.value;
    this.details = await this.fetchRoutingDetails(role.id);

    const selectedUsers = this.users.filter(user => this.details.users.find(u => u.user_id === user.id));
    for (const user of selectedUsers) {
      const tempUser = this.details.users.find(u => u.user_id === user.id);
      user.initialSalePoints = tempUser.sale_points;
      user.numberOfSalePoints = tempUser.sale_points.length;
    }
    this.userSelection = new SelectionModel(true, selectedUsers);
  }


  close(shouldReload?: boolean) {
    this.matDialogRef.close(shouldReload);
  }

  async fetchSalePointsForUser(row: User, shouldAdd = false) {
    this.highlight(row);
    this.selectedUser = row;
    this.salePointsLoading = true;
    this.salePointsDataSource = new MatTableDataSource<SalePoints>();
    if (shouldAdd || !this.userSelection.isSelected(row)) {
      this.userSelection.toggle(row);
    }

    try {
      if (this.categoryId !== 3) {
        await this.calculateUserSalePoints(row);
      }

    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    } finally {
      this.salePointsLoading = false;
    }
  }

  async calculateUserSalePoints(row: User) {

    const data = this.salePoints.filter(sp => sp.user_id === row.id);

    if (data.length === 0) {
      let filter = this.filter + ' = ' + row.id;
      if (this.categoryId === 1) {
        filter += ' AND !(account_group_id<=>3000) AND !(account_group_id<=>3009)';
      } else if (this.categoryId === 2) {
        filter += ' AND (account_group_id=3000 OR account_group_id=3009)';
      }
      const result = await this.commonService.getListWithParameters(`sale_point/simple`, filter) as SalePoints[];
      for (const salePoint of result) {
        const model: SalePoints = {
          id: salePoint.id,
          customer_name: salePoint.customer_name,
          user_id: row.id,
          name: row.name
        };
        data.push(model);
      }
    }
    this.salePoints = this.salePoints.concat(data);
    this.salePointsDataSource = new MatTableDataSource<SalePoints>(data);
    this.salePointsDataSource.paginator = this.spPaginator;
    this.salePoints = [];

    const tempUser = this.details && this.details.users.find(u => u.user_id === row.id) ?
      this.details.users.find(u => u.user_id === row.id) : this.userSelection.selected.find(u => u.id === row.id);
    row.sale_points = tempUser && tempUser.sale_points.length > 0 ?
      data.filter(sp => tempUser.sale_points.find(temp => temp.id === sp.id)) as any : [];
    row.numberOfSalePoints = tempUser && tempUser.sale_points.length > 0 ? tempUser.sale_points.length : 0;
    this.salePointSelection = new SelectionModel<SalePoints>(true, row.sale_points);
  }

  editUserSalePoint(row) {
    this.highlight(row);
    this.salePointSelection.toggle(row);
    const user = this.usersDataSource.data.find(u => u.id === row.user_id);
    if (user) {
      user.sale_points = this.salePointSelection.selected;
      user.numberOfSalePoints = user.sale_points.length;
    }
    if (!this.salePointSelection.isSelected(row)) {
      return;
    }
  }

  searchUsers(target: any) {
    this.usersDataSource.filter = target.value.trim();
  }

  searchSalePoint(target: any) {
    this.salePointsDataSource.filter = target.value.trim();
  }

  async submitRequest(roleId: number, requestObject: any) {
    try {
      await this.commonService.updateObject(`questionnaire/sending/${this.detailsId}/${roleId}`, requestObject);
      this.mode === 1 ? this.toastMessagesService.toastMessages(`Η ενημέρωση ολοκληρώθηκε με επιτυχία`) : this.toastMessagesService.toastMessages(`Η ανάθεση ολοκληρώθηκε με επιτυχία`);

      this.matDialogRef.close(true);
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    }
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.salePointSelection.clear();
      const user = this.usersDataSource.data.find(u => u.id === this.selectedUser.id);
      if (user) {
        user.sale_points = [];
        user.numberOfSalePoints = user.sale_points.length;
      }
    } else {
      const data = this.salePointsDataSource.data as SalePoints[];
      data.forEach((row) => this.salePointSelection.select(row));

      const user = this.usersDataSource.data.find(u => u.id === this.selectedUser.id);
      if (user) {
        user.sale_points = this.salePointSelection.selected;
        user.numberOfSalePoints = user.sale_points.length;
      }
    }
  }

  isAllSelected(): boolean {
    const numSelected = this.salePointSelection.selected.length;

    // @ts-ignore
    const numRows = this.salePointsDataSource.data.length;

    return numSelected === numRows;
  }
}
